import { AnimatePresence, motion } from 'framer-motion';
import {
  AlarmClockPlus,
  BadgeCheck,
  Bolt,
  CircleHelp,
  Clock3,
  Cloud,
  CloudOff,
  Flame,
  Lightbulb,
  LoaderCircle,
  RotateCcw,
  Send,
  Trophy,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { useCountdown } from '../hooks/useCountdown';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import type { ArenaEvent, PublicQuestion, TeamPublic, TeamState } from '../types/arena';

const difficultyLabel = { easy: 'آسان', medium: 'متوسط', hard: 'سخت' } as const;
const difficultyClasses = {
  easy: 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/20',
  medium: 'bg-amber-400/10 text-amber-300 ring-amber-400/20',
  hard: 'bg-red-400/10 text-red-300 ring-red-400/20',
} as const;

interface Feedback {
  kind: 'success' | 'error' | 'power';
  title: string;
  detail: string;
}

function formatTime(ms: number) {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60).toLocaleString('fa-IR')}:${String(total % 60).padStart(2, '0')}`;
}

function PowerButton({ icon, title, count, disabled, onClick }: { icon: React.ReactNode; title: string; count: number; disabled: boolean; onClick: () => void }) {
  return (
    <button disabled={disabled} onClick={onClick} className="group flex w-full items-center gap-3 rounded-xl border border-white/8 bg-white/[.035] p-3 text-right transition hover:border-brand/30 hover:bg-brand/[.06] disabled:opacity-35">
      <span className="grid size-9 place-items-center rounded-lg bg-white/[.06] text-brand transition group-hover:bg-brand group-hover:text-white">{icon}</span>
      <span className="min-w-0 flex-1">
        <b className="block text-xs">{title}</b>
        <small className="text-[9px] text-white/35">باقی‌مانده {count.toLocaleString('fa-IR')}</small>
      </span>
      <span className="font-mono text-xs text-white/30">×{count}</span>
    </button>
  );
}

export function ArenaPage({ event, team, state, question, rank }: { event: ArenaEvent; team: TeamPublic; state: TeamState; question: PublicQuestion | null; rank: number }) {
  const [answer, setAnswer] = useState(state.draftAnswer || '');
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'offline' | 'error'>('saved');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const questionId = state.currentQuestionId;
  const previousQuestion = useRef<string | null>(questionId);
  const expiredFor = useRef<string | null>(null);
  const remainingMs = useCountdown(state.deadlineMs, event.status === 'running');
  const locked = event.status !== 'running' || !question || submitting || state.awaitingRetryDecision;
  const lowTime = remainingMs > 0 && remainingMs <= 30_000;

  useEffect(() => {
    if (questionId && previousQuestion.current !== questionId) {
      previousQuestion.current = questionId;
      expiredFor.current = null;
      setAnswer(state.draftAnswer || '');
      setHint(null);
    }
  }, [questionId, state.draftAnswer]);

  useEffect(() => {
    if (!questionId || answer === state.draftAnswer || event.status === 'finished') return;
    setSaveStatus(navigator.onLine ? 'saving' : 'offline');
    const timer = window.setTimeout(() => {
      if (!navigator.onLine) return;
      void callFunction<{ answer: string }, { savedAtMs: number }>('updateDraft', { answer })
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, 900);
    return () => window.clearTimeout(timer);
  }, [answer, event.status, questionId, state.draftAnswer]);

  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden' && answer !== state.draftAnswer) {
        void callFunction('updateDraft', { answer }).catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [answer, state.draftAnswer]);

  useEffect(() => {
    if (event.status === 'running' && questionId && remainingMs === 0 && expiredFor.current !== questionId) {
      expiredFor.current = questionId;
      void callFunction<Record<string, never>, { expired: boolean }>('expireQuestion', {})
        .then((result) => result.expired && setFeedback({ kind: 'error', title: 'زمان تمام شد', detail: 'این راند بدون امتیاز ثبت شد.' }))
        .catch(() => {
          expiredFor.current = null;
        });
    }
  }, [event.status, questionId, remainingMs]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await callFunction<{ answer: string; submissionId: string }, { correct?: boolean; verdict: string; scoreAwarded?: number; retryOffered?: boolean; unlocked?: string[] }>('submitAnswer', {
        answer,
        submissionId: crypto.randomUUID(),
      });
      if (result.correct) {
        const unlockNames: Record<string, string> = { hint: 'راهنما', extraTime: '۳۰ ثانیه اضافه', retry: 'فرصت دوباره' };
        const unlockedLabel = result.unlocked?.map((item) => unlockNames[item] ?? item).join(' و ');
        setFeedback(
          unlockedLabel
            ? { kind: 'power', title: 'جایزه آزاد شد!', detail: `${unlockedLabel} به کوله‌پشتی تیم اضافه شد.` }
            : { kind: 'success', title: result.scoreAwarded === 300 ? 'ترکوندید!' : 'درست بود؛ امتیاز گرفتید!', detail: `${(result.scoreAwarded ?? 0).toLocaleString('fa-IR')} امتیاز به تیم اضافه شد.` },
        );
      } else if (result.retryOffered) {
        setFeedback({ kind: 'power', title: 'یک جان اضافه دارید', detail: 'پاسخ درست نبود؛ می‌توانید یک بار دیگر جواب بدهید.' });
      } else {
        const labels: Record<string, string> = {
          wrong_answer: 'پاسخ با جواب داور یکی نبود.',
          empty_answer: 'پاسخ خالی قابل ثبت نیست.',
        };
        setFeedback({ kind: 'error', title: 'این یکی به هدف نخورد', detail: labels[result.verdict] ?? 'این تلاش بدون امتیاز ثبت شد.' });
      }
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'ثبت پاسخ به مشکل خورد', detail: errorMessage(reason) });
    } finally {
      setSubmitting(false);
    }
  };

  const power = async (action: 'hint' | 'extra-time' | 'retry' | 'decline-retry') => {
    try {
      const result = await callFunction<{ action: string }, { hint?: string }>('usePowerUp', { action });
      if (action === 'hint' && result.hint) setHint(result.hint);
      if (action === 'extra-time') setFeedback({ kind: 'power', title: '۳۰ ثانیه اضافه شد', detail: 'زمان جدید ثبت شد؛ نفس تازه کنید و ادامه دهید.' });
      if (action === 'retry') setFeedback({ kind: 'power', title: 'فرصت دوباره فعال شد', detail: 'در تلاش دوم حداکثر ۵۰٪ امتیاز می‌گیرید.' });
      if (action === 'decline-retry') setFeedback(null);
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'جایزه فعال نشد', detail: errorMessage(reason) });
    }
  };

  return (
    <main className="h-screen min-h-[650px] overflow-hidden bg-ink text-white" dir="rtl">
      <header className="flex h-[70px] items-center border-b border-white/8 bg-[#0B101B] px-5">
        <BrandLockup inverse />
        <div className="mx-6 h-7 w-px bg-white/10" />
        <div className="flex min-w-0 items-center gap-3">
          <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-10 rounded-xl" />
          <div className="min-w-0">
            <b className="block truncate text-sm">{team.name}</b>
            <span className="block truncate text-[9px] text-white/35">{team.members.join('، ')}</span>
          </div>
        </div>
        <div className="mr-auto flex items-center gap-6">
          <div className="text-left">
            <span className="block text-[9px] text-white/35">امتیاز</span>
            <motion.strong key={team.score} initial={{ scale: 1.4, color: '#F26A21' }} animate={{ scale: 1, color: '#FFFFFF' }} className="font-mono text-lg">
              {team.score.toLocaleString('fa-IR')}
            </motion.strong>
          </div>
          <div className="text-left">
            <span className="block text-[9px] text-white/35">رتبه فعلی</span>
            <strong className="flex items-center gap-1 text-lg">
              <Trophy className="size-4 text-amber-400" /> {rank.toLocaleString('fa-IR')}
            </strong>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-white/[.04] px-3 py-2 text-[10px] text-white/55">
            {navigator.onLine ? <Cloud className="size-4 text-emerald-400" /> : <CloudOff className="size-4 text-red-400" />}
            {saveStatus === 'saving' ? 'در حال ذخیره…' : saveStatus === 'saved' ? 'پاسخ ذخیره شد' : saveStatus === 'offline' ? 'منتظر اتصال' : 'خطا در ذخیره'}
          </div>
        </div>
      </header>

      <div className="grid h-[calc(100vh-70px)] min-h-[580px] grid-cols-[minmax(330px,.9fr)_minmax(520px,1.45fr)_220px]" dir="rtl">
        <motion.section key={question?.id ?? 'loading'} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} className={cn('scrollbar-thin overflow-y-auto border-l border-white/8 bg-[#101726] p-5', question?.difficulty === 'hard' && 'bg-[radial-gradient(circle_at_top_right,rgba(239,68,68,.11),transparent_46%)]')}>
          {question ? (
            <>
              <div className="flex items-center justify-between">
                <span className={cn('rounded-full px-3 py-1 text-[10px] font-black ring-1', difficultyClasses[question.difficulty])}>{difficultyLabel[question.difficulty]}</span>
                <span className="font-mono text-[11px] text-white/35" dir="ltr">
                  Q {String(state.currentIndex + 1).padStart(2, '0')} / 10
                </span>
              </div>
              <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/8">
                <motion.div className="h-full rounded-full bg-brand" initial={false} animate={{ width: `${((state.currentIndex + 1) / 10) * 100}%` }} />
              </div>
              <h1 className="mt-7 text-2xl font-black leading-tight">{question.title}</h1>
              <p className="mt-4 whitespace-pre-line text-sm leading-8 text-slate-300">{question.prompt}</p>
              <div className="mt-6 rounded-2xl border border-brand/20 bg-brand/[.055] p-4">
                <span className="block text-[10px] font-black text-brand">فرمت پاسخ</span>
                <p className="mt-2 text-xs leading-6 text-white/65">{question.answerFormat}</p>
              </div>
              <div className="mt-7 flex flex-wrap gap-2">
                {question.topics.slice(0, 4).map((topic) => (
                  <span key={topic} className="rounded-md bg-white/[.045] px-2 py-1 font-mono text-[9px] text-white/45" dir="ltr">
                    {topic}
                  </span>
                ))}
              </div>
              {hint && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 border-r-2 border-amber-400 bg-amber-400/[.06] p-4 text-xs leading-6 text-amber-100">
                  <span className="mb-1 flex items-center gap-2 font-black text-amber-300">
                    <Lightbulb className="size-4" /> راهنما
                  </span>
                  {hint}
                </motion.div>
              )}
              <div className="mt-8 grid grid-cols-2 gap-3 border-t border-white/8 pt-5">
                <div>
                  <span className="text-[9px] text-white/35">امتیاز سؤال</span>
                  <b className="mt-1 block font-mono text-xl text-brand">{question.points}</b>
                </div>
                <div>
                  <span className="text-[9px] text-white/35">زنجیره درست‌ها</span>
                  <b className="mt-1 flex items-center gap-1 text-xl">
                    <Flame className={cn('size-5', team.streak ? 'text-brand' : 'text-slate-600')} /> ×{team.streak.toLocaleString('fa-IR')}
                  </b>
                </div>
              </div>
            </>
          ) : (
            <div className="grid h-full place-items-center">
              <LoaderCircle className="size-6 animate-spin text-brand" />
            </div>
          )}
        </motion.section>

        <section className="flex min-w-0 flex-col bg-[#0B101B] p-5">
          <div className="flex items-center justify-between rounded-t-3xl border border-white/[.07] bg-white/[.025] px-5 py-4">
            <div>
              <span className="text-[10px] font-bold text-white/40">Answer Check Mode</span>
              <h2 className="mt-1 text-lg font-black">پاسخ نهایی تیم</h2>
            </div>
            <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-[10px] font-bold text-emerald-300">بدون اجرای Query</span>
          </div>
          <div className="min-h-0 flex-1 border-x border-white/[.07] bg-[#0E1523] p-5">
            <textarea
              dir="auto"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              readOnly={locked}
              placeholder={question?.answerFormat ?? 'پاسخ نهایی را اینجا بنویسید'}
              className="h-full min-h-[300px] w-full resize-none rounded-2xl border border-white/8 bg-[#090F1A] p-5 text-left font-mono text-lg leading-8 text-white outline-none transition placeholder:text-white/20 focus:border-brand/60 disabled:opacity-50"
            />
          </div>
          <div className="flex items-center justify-between rounded-b-3xl border border-white/[.07] bg-[#111827] px-5 py-4">
            <p className="max-w-[55%] text-[10px] leading-5 text-white/35">سیستم فقط جواب نهایی را با جواب داور مقایسه می‌کند. Query اجرا نمی‌شود و دیتابیس AdventureWorks لازم نیست.</p>
            <Button size="lg" disabled={locked || submitting || !answer.trim()} onClick={() => void submit()}>
              {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />} ثبت پاسخ نهایی
            </Button>
          </div>
        </section>

        <aside className="border-r border-white/8 bg-[#0E1523] p-4">
          <div className={cn('rounded-2xl border p-4 text-center transition', lowTime ? 'border-red-500/40 bg-red-500/8' : 'border-white/8 bg-white/[.025]')}>
            <span className="flex items-center justify-center gap-2 text-[10px] text-white/40">
              <Clock3 className="size-3.5" /> زمان باقی‌مانده
            </span>
            <strong className={cn('mt-2 block font-mono text-4xl tabular-nums', lowTime ? 'text-red-400' : 'text-white')} dir="ltr">
              {formatTime(remainingMs)}
            </strong>
            <div className="mt-3 h-1 overflow-hidden rounded bg-white/8">
              <motion.div className={cn('h-full origin-left', lowTime ? 'bg-red-500' : 'bg-brand')} animate={{ width: `${Math.max(0, Math.min(100, remainingMs / ((question?.timeLimitSec ?? 1) * 10)))}%` }} />
            </div>
          </div>
          <div className="mt-5">
            <span className="mb-3 block text-[9px] font-bold tracking-wider text-white/30">جایزه‌ها</span>
            <div className="space-y-2">
              <PowerButton icon={<Lightbulb className="size-4" />} title="راهنما" count={state.powerUps.hint} disabled={locked || state.powerUps.hint < 1 || state.usedHint} onClick={() => void power('hint')} />
              <PowerButton icon={<AlarmClockPlus className="size-4" />} title="۳۰ ثانیه اضافه" count={state.powerUps.extraTime} disabled={locked || state.powerUps.extraTime < 1} onClick={() => void power('extra-time')} />
              <PowerButton icon={<RotateCcw className="size-4" />} title="فرصت دوباره" count={state.powerUps.retry} disabled={!state.awaitingRetryDecision || state.powerUps.retry < 1} onClick={() => void power('retry')} />
            </div>
          </div>
          <div className="mt-5 border-t border-white/8 pt-4">
            <span className="text-[9px] text-white/30">مسیر تیم</span>
            <div className="mt-3 grid grid-cols-5 gap-1.5">
              {Array.from({ length: 10 }, (_, index) => (
                <span key={index} className={cn('h-1.5 rounded-full', index < state.currentIndex ? 'bg-emerald-400' : index === state.currentIndex ? 'bg-brand' : 'bg-white/8')} />
              ))}
            </div>
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {event.status === 'countdown' && <CountdownOverlay startsAtMs={event.startsAtMs} />}
        {event.status === 'paused' && <PauseOverlay connection={event.pausedReason === 'connection'} />}
        {state.awaitingRetryDecision && (
          <div className="fixed inset-x-0 bottom-5 z-50 mx-auto flex w-fit items-center gap-4 rounded-2xl border border-amber-400/25 bg-[#171B25]/95 p-4 shadow-2xl backdrop-blur">
            <CircleHelp className="size-6 text-amber-300" />
            <div>
              <b className="block text-sm">یک شانس دیگر می‌خواهید؟</b>
              <span className="text-[10px] text-white/45">تلاش دوم تا ۵۰٪ امتیاز دارد.</span>
            </div>
            <Button size="sm" onClick={() => void power('retry')}>
              بله، دوباره می‌زنیم
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void power('decline-retry')}>
              نه، سؤال بعدی
            </Button>
          </div>
        )}
        {feedback && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} className={cn('fixed bottom-6 left-1/2 z-[60] flex min-w-[360px] -translate-x-1/2 items-center gap-4 rounded-2xl border bg-[#151C2A]/95 p-4 shadow-2xl backdrop-blur', feedback.kind === 'success' ? 'border-emerald-400/30' : feedback.kind === 'power' ? 'border-amber-400/30' : 'border-red-400/25')}>
            <span className={cn('grid size-11 place-items-center rounded-xl', feedback.kind === 'success' ? 'bg-emerald-400/12 text-emerald-300' : feedback.kind === 'power' ? 'bg-amber-400/12 text-amber-300' : 'bg-red-400/12 text-red-300')}>
              {feedback.kind === 'success' ? <BadgeCheck /> : feedback.kind === 'power' ? <Bolt /> : <X />}
            </span>
            <div className="flex-1">
              <b className="block text-sm">{feedback.title}</b>
              <span className="mt-1 block text-[10px] text-white/50">{feedback.detail}</span>
            </div>
            <button onClick={() => setFeedback(null)} className="text-white/30 hover:text-white">
              <X className="size-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function CountdownOverlay({ startsAtMs }: { startsAtMs: number | null }) {
  const remaining = useCountdown(startsAtMs, true);
  const count = Math.max(1, Math.ceil(remaining / 1000));
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-ink/88 backdrop-blur-md">
      <motion.div key={count} initial={{ scale: 0.4, opacity: 0, rotate: -8 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} exit={{ scale: 1.6, opacity: 0 }} className="relative text-center">
        <span className="absolute inset-1/2 size-52 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand/25" />
        <strong className="relative font-mono text-[9rem] font-black leading-none text-brand">{count.toLocaleString('fa-IR')}</strong>
        <p className="mt-7 text-sm font-bold text-white/55">آماده باشید؛ بازی از همین لحظه شروع می‌شود</p>
      </motion.div>
    </motion.div>
  );
}

function PauseOverlay({ connection }: { connection: boolean }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 grid place-items-center bg-ink/74 backdrop-blur-sm">
      <div className="max-w-lg text-center">
        <span className="mx-auto grid size-16 place-items-center rounded-2xl border border-brand/25 bg-brand/10">
          <CloudOff className="size-7 text-brand" />
        </span>
        <h2 className="mt-6 text-2xl font-black">بازی موقتاً مکث شد</h2>
        <p className="mt-3 leading-7 text-slate-300">{connection ? 'یکی از تیم‌ها از خط خارج شده است؛ بازی تا برگشت تیم مکث می‌ماند.' : 'مدیر مسابقه بازی را مکث کرده است. پاسخ و زمان شما محفوظ است.'}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-xs text-white/40">
          <LoaderCircle className="size-4 animate-spin" /> منتظر سوت ادامه
        </span>
      </div>
    </motion.div>
  );
}
